import React from 'react';
import "./smile-pages.css";

export default function HowDoesSmilingBoostYourImmuneSystem() {
  return (
    <div className="container-box mt-5 mb-5 smile-page">

      <h1>How does Smiling Boost Your Immune System?</h1>

      <p>
        Smiling boosts your immune system by reducing stress, enhancing mood-regulating chemicals,
        and improving the body’s physiological responses — all of which <strong>help your
        immune system </strong> work more efficiently to <strong>fight illness and maintain overall health.</strong>
      </p>

      <p>Here’s how smiling supports immune function, backed by research:</p>

      <hr />

      {/* Section 1 */}
      <h3>🧠 1. Smiling Reduces Stress Hormones That Suppress Immunity</h3>

      <p>
        Chronic stress increases <strong>cortisol</strong>, a hormone that:
      </p>

      <ul>
        <li>Weakens immune response</li>
        <li>Suppresses production of white blood cells</li>
        <li>Increases inflammation in the body</li>
      </ul>

      <p>
        Smiling reduces cortisol levels, helping your immune system recover and function more effectively.
      </p>

      <p><strong>📚 Study:</strong><br />
        Pressman & Cohen (2005) found that <strong>positive emotional expressions</strong>, like smiling
        and laughter, <strong>buffer the immune system</strong> against stress.
      </p>

      <hr />

      {/* Section 2 */}
      <h3>😊 2. Smiling Triggers “Feel-Good” Chemicals That Support Immune Function</h3>

      <p>When you smile, your brain releases:</p>

      <ul>
        <li><strong>Endorphins</strong> — pain relievers and mood boosters</li>
        <li><strong>Dopamine</strong> — promotes motivation and pleasure</li>
        <li><strong>Serotonin</strong> — regulates mood and strengthens resilience</li>
        <li><strong>Oxytocin</strong> — enhances social bonding and reduces inflammation</li>
      </ul>

      <p>These chemicals have been shown to:</p>

      <ul>
        <li>Reduce the body’s inflammatory response</li>
        <li>Enhance immune cell activity, such as natural killer (NK) cells and T-cells</li>
        <li>Improve resistance to illness</li>
      </ul>

      <p><strong>📚 Study Insight:</strong><br />
        Cohen et al. (2006) found that individuals who experienced more <strong>positive emotions </strong>
        were <strong>less likely to get sick</strong> after being exposed to a common cold virus.
      </p>

      <hr />

      {/* Section 3 */}
      <h3>🧬 3. Smiling Supports Parasympathetic Nervous System (Rest and Repair)</h3>

      <p>
        Smiling — especially when genuine — helps activate the
        <strong> parasympathetic nervous system</strong>, which governs:
      </p>

      <ul>
        <li>Rest</li>
        <li>Recovery</li>
        <li>Immune regulation</li>
      </ul>

      <p>
        This allows your body to <strong>focus on repair</strong>, digestion, and 
        <strong> immune defense</strong> — rather than staying in “fight-or-flight” mode,
        which can wear down your immunity over time.
      </p>

      <hr />

      {/* Section 4 */}
      <h3>🤝 4. Smiling Encourages Social Connection — Which Strengthens Immunity</h3>

      <p>
        Positive social interactions (often sparked by smiling) are linked to:
      </p>

      <ul>
        <li>Lower inflammation markers</li>
        <li>Increased immune cell production</li>
        <li>Better outcomes during illness or recovery</li>
      </ul>

      <p>
        Humans are social creatures, and feeling connected improves
        <strong> emotional health</strong>, which directly boosts immune strength.
      </p>

      <p><strong>📚 Harvard Study:</strong><br />
        The Harvard Women’s Health Watch reported that 
        <strong> social connections improve health and increase longevity</strong>,
        partly due to stronger immune resilience.
      </p>

      <hr />

      {/* Summary Table */}
      <h3>✅ Summary: How Smiling Boosts Your Immune System</h3>

      <table className="table mt-3">
        <thead>
          <tr>
            <th>Biological Effect</th>
            <th>Immune Benefit</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Lowers cortisol (stress hormone)</td>
            <td>Reduces immune suppression</td>
          </tr>
          <tr>
            <td>Releases mood-enhancing chemicals</td>
            <td>Increases immune cell activity</td>
          </tr>
          <tr>
            <td>Activates parasympathetic system</td>
            <td>Promotes healing and cellular repair</td>
          </tr>
          <tr>
            <td>Enhances social connection</td>
            <td>Lowers inflammation, boosts resilience</td>
          </tr>
          <tr>
            <td>Improves emotional regulation</td>
            <td>Supports overall immune balance</td>
          </tr>
        </tbody>
      </table>

      <hr />

      {/* Keys to Remember */}
      <h3>🔑 Keys to Remember</h3>

      <p>
        <strong>
        Smiling supports your immune system by helping your body stay balanced, calm, and emotionally strong.
        </strong> Even when you don’t feel like smiling, choosing to smile can still activate these protective effects —
        and over time, build real improvements in your physical health.
        </p>
        <p>
         <strong> That Smile App is here to help boost your immune system.</strong>
      </p>

    </div>
  );
}
